import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { euro } from '../../lib/format';
import { fileToResizedDataURL } from '../../lib/image';
import { useToasts } from '../../lib/toast';
import type { OptionGroupDto } from '@taj/types';

const toCents = (s: string | null) => Math.round(parseFloat((s ?? '0').replace(',', '.')) * 100) || 0;
const parseAllergens = (s: string | null) => (s ?? '').split(',').map((x) => x.trim()).filter(Boolean);

export function OptionsEditor({
  productId,
  productName,
  onClose,
}: {
  productId: string;
  productName: string;
  onClose: () => void;
}) {
  const push = useToasts((s) => s.push);
  const [groups, setGroups] = useState<OptionGroupDto[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const targetRef = useRef<string | null>(null);

  const reload = () =>
    api.getMenu().then((m) => {
      const p = m.flatMap((c) => c.products).find((x) => x.id === productId);
      setGroups(p?.optionGroups ?? []);
    });
  useEffect(() => { reload(); }, [productId]);

  const wrap = async (fn: () => Promise<unknown>) => {
    try { await fn(); await reload(); } catch (e: any) { push(e.message, 'warn'); }
  };

  const addGroup = () => {
    const name = prompt('Nom du groupe (ex : Sauces, Suppléments) ?');
    if (!name?.trim()) return;
    const max = parseInt(prompt('Nombre maximum de choix autorisés ?', '1') || '1', 10) || 1;
    wrap(() => api.createOptionGroup({ productId, name: name.trim(), minSelect: 0, maxSelect: max }));
  };
  const renameGroup = (g: OptionGroupDto) => {
    const name = prompt('Nom du groupe', g.name);
    if (!name?.trim()) return;
    wrap(() => api.updateOptionGroup(g.id, { name: name.trim() }));
  };
  const setMax = (g: OptionGroupDto) => {
    const v = prompt(`Nombre maximum de choix pour « ${g.name} »`, String(g.maxSelect));
    if (v === null) return;
    const max = parseInt(v, 10);
    if (!Number.isFinite(max) || max < 1) return push('Nombre invalide', 'warn');
    wrap(() => api.updateOptionGroup(g.id, { maxSelect: max }));
  };
  const delGroup = (g: OptionGroupDto) => {
    if (!confirm(`Supprimer le groupe « ${g.name} » et toutes ses options ?`)) return;
    wrap(() => api.deleteOptionGroup(g.id));
  };
  const addOption = (g: OptionGroupDto) => {
    const name = prompt(`Nouvelle option dans « ${g.name} » ?`);
    if (!name?.trim()) return;
    const priceDelta = toCents(prompt('Supplément de prix en € (0 si gratuit)', '0'));
    const allergens = parseAllergens(prompt('Allergènes (séparés par virgule, vide si aucun)', ''));
    wrap(() => api.createOption({ groupId: g.id, name: name.trim(), priceDelta, allergens }));
  };
  const editOption = (o: { id: string; name: string; priceDelta: number; allergens: string[] }) => {
    const name = prompt('Nom de l’option', o.name);
    if (!name?.trim()) return;
    const priceDelta = toCents(prompt('Supplément de prix en €', (o.priceDelta / 100).toFixed(2)));
    const allergens = parseAllergens(prompt('Allergènes (séparés par virgule)', o.allergens.join(', ')));
    wrap(() => api.updateOption(o.id, { name: name.trim(), priceDelta, allergens }));
  };
  const delOption = (id: string) => wrap(() => api.deleteOption(id));

  const pickPhoto = (optionId: string) => { targetRef.current = optionId; fileRef.current?.click(); };
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !targetRef.current) return;
    try {
      const dataUrl = await fileToResizedDataURL(file, 240, 0.8);
      await api.updateOption(targetRef.current, { imageUrl: dataUrl });
      push('📷 Photo ajoutée');
      reload();
    } catch {
      push('Impossible de charger cette photo', 'warn');
    }
  };
  const removePhoto = (id: string) => wrap(() => api.updateOption(id, { imageUrl: '' }));

  return (
    <div className="al-overlay" onClick={onClose}>
      <div className="al-sheet" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '92dvh' }}>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
        <div className="al-grip" />
        <div className="al-head">
          <div className="ic">⚙️</div>
          <div>
            <div className="ti">Options & suppléments</div>
            <div className="su">{productName}</div>
          </div>
          <button className="al-close" onClick={onClose}>✕</button>
        </div>

        <div className="al-grid" style={{ display: 'block' }}>
          {groups.length === 0 && <div className="no-data">Aucun groupe d’options. Ajoute-en un ci-dessous.</div>}
          {groups.map((g) => (
            <div key={g.id} className="cc" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ fontWeight: 900, flex: 1 }}>{g.name}</span>
                <button className="linkbtn" onClick={() => setMax(g)}>max : {g.maxSelect}</button>
                <button className="cr-btn" onClick={() => renameGroup(g)}>✏️</button>
                <button className="cr-btn del" onClick={() => delGroup(g)}>🗑</button>
              </div>
              {g.options.map((o) => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                  {o.imageUrl ? (
                    <img
                      src={o.imageUrl}
                      title="Changer la photo"
                      onClick={() => pickPhoto(o.id)}
                      style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', cursor: 'pointer', flexShrink: 0 }}
                    />
                  ) : (
                    <button className="cr-btn" title="Ajouter une photo" onClick={() => pickPhoto(o.id)}>📷</button>
                  )}
                  <span style={{ flex: 1, fontWeight: 700, fontSize: '.86rem' }}>
                    {o.name} {o.allergens.length > 0 && <span style={{ color: 'var(--cuisine)' }}>⚠️</span>}
                  </span>
                  <span style={{ fontSize: '.8rem', color: 'var(--ink-dim)', fontWeight: 700 }}>
                    {o.priceDelta > 0 ? `+ ${euro(o.priceDelta)}` : 'gratuit'}
                  </span>
                  {o.imageUrl && <button className="cr-btn" title="Retirer la photo" onClick={() => removePhoto(o.id)}>🚫</button>}
                  <button className="cr-btn" onClick={() => editOption(o)}>✏️</button>
                  <button className="cr-btn del" onClick={() => delOption(o.id)}>🗑</button>
                </div>
              ))}
              <button className="linkbtn" style={{ marginTop: 8, color: 'var(--gold)' }} onClick={() => addOption(g)}>
                ＋ Ajouter une option
              </button>
            </div>
          ))}
        </div>

        <button className="al-go" onClick={addGroup}>＋ AJOUTER UN GROUPE (Sauces, Suppléments…)</button>
      </div>
    </div>
  );
}
